import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Textarea } from './ui/textarea';
import { Check, X, Clock, User } from 'lucide-react';
import { toast } from 'sonner';
import { dateLocale } from '../lib/dateFormat';
import { DateText } from './ui/week-badge';
import { fmtDateWeek } from '../lib/dateFormat';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format values for display - handles objects, dates, arrays, etc.
const formatValue = (value) => {
  if (value === null || value === undefined) {
    return <em>{i18n.t('auto.misc.empty')}</em>;
  }
  if (typeof value === 'object') {
    // Handle Date objects
    if (value instanceof Date) {
      return format(value, 'yyyy.MM.dd');
    }
    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return <em>{i18n.t('auto.misc.empty')}</em>;
      // For certificate arrays, format nicely
      return value.map(item => {
        if (typeof item === 'object' && item.type) {
          return item.type + (item.expiry_date ? ` (exp: ${fmtDateWeek(item.expiry_date)})` : '');
        }
        return JSON.stringify(item);
      }).join(', ');
    }
    // Handle emergency_contact and other objects
    const entries = Object.entries(value).filter(([k, v]) => v);
    if (entries.length === 0) return <em>{i18n.t('auto.misc.empty')}</em>;
    return entries.map(([k, v]) => `${k}: ${v}`).join(', ');
  }
  // Handle date strings
  if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
    return <DateText value={value} />;
  }
  return String(value);
};

const ProfileChangeRequests = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/profile-changes`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching profile change requests:', error);
      toast.error(t('auto.misc.failedToLoadProfileChangeRequests'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessing(true);
    try {
      await axios.put(`${API}/profile-changes/${requestId}/approve`);
      toast.success(t('auto.misc.profileChangesApprovedAndApplied'));
      fetchRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve changes');
    } finally {
      setProcessing(false);
    }
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectComment('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      await axios.put(`${API}/profile-changes/${selectedRequest.id}/reject?comment=${encodeURIComponent(rejectComment)}`);
      toast.success(t('auto.misc.profileChangeRequestRejected'));
      setRejectDialogOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject changes');
    } finally {
      setProcessing(false);
    }
  };

  const formatFieldName = (field) => {
    return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { color: 'bg-green-100 text-green-800', icon: Check },
      rejected: { color: 'bg-red-100 text-red-800', icon: X },
    };
    const { color, icon: Icon } = config[status] || config.pending;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => r.status !== 'pending');

  if (loading) {
    return <div className="p-4 text-center">{t('auto.misc.loadingProfileChangeRequests')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Pending Requests */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-600" />
            <CardTitle className="text-lg">Pending Profile Changes ({pendingRequests.length})</CardTitle>
          </div>
          <CardDescription>{t('auto.misc.reviewAndApproveOrRejectEmployee')}</CardDescription>
        </CardHeader>
        <CardContent>
          {pendingRequests.length === 0 ? (
            <p className="text-slate-500 text-center py-4">{t('auto.misc.noPendingProfileChangeRequests')}</p>
          ) : (
            <div className="space-y-4">
              {pendingRequests.map((request) => (
                <Card key={request.id} className="border-yellow-200 bg-yellow-50/50">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-lg">{request.employee_name || 'Unknown Employee'}</p>
                          <div className="text-sm text-slate-500 space-x-2">
                            {request.employee_department && <span>{request.employee_department}</span>}
                            {request.employee_department && request.employee_position && <span>•</span>}
                            {request.employee_position && <span>{request.employee_position}</span>}
                          </div>
                          {request.employee_email && (
                            <p className="text-sm text-slate-400">{request.employee_email}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            Requested: {new Date(request.requested_at).toLocaleString(dateLocale())}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-600 hover:bg-green-50"
                          onClick={() => handleApprove(request.id)}
                          disabled={processing}
                        >
                          <Check className="w-4 h-4 mr-1" /> {t('auto.misc.approve')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-600 hover:bg-red-50"
                          onClick={() => openRejectDialog(request)}
                          disabled={processing}
                        >
                          <X className="w-4 h-4 mr-1" /> {t('auto.misc.reject')}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <p className="text-sm font-medium text-slate-700 mb-2">{t('auto.misc.requestedChanges')}</p>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('auto.misc.field')}</TableHead>
                            <TableHead>{t('auto.misc.currentValue')}</TableHead>
                            <TableHead>{t('auto.misc.newValue')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(request.requested_changes).map(([field, newValue]) => (
                            <TableRow key={field}>
                              <TableCell className="font-medium">{formatFieldName(field)}</TableCell>
                              <TableCell className="text-slate-500">
                                {formatValue(request.original_values[field])}
                              </TableCell>
                              <TableCell className="text-green-700 font-medium">
                                {formatValue(newValue)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Processed Requests History */}
      {processedRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('auto.misc.requestHistory')}</CardTitle>
            <CardDescription>{t('auto.misc.previouslyProcessedProfileChangeRequests')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('auto.misc.employee')}</TableHead>
                  <TableHead>{t('auto.misc.changes')}</TableHead>
                  <TableHead>{t('auto.misc.requested')}</TableHead>
                  <TableHead>{t('auto.misc.status')}</TableHead>
                  <TableHead>{t('auto.misc.reviewed')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {processedRequests.slice(0, 10).map((request) => (
                  <TableRow key={request.id}>
                    <TableCell className="font-medium">{request.employee_name}</TableCell>
                    <TableCell>
                      {Object.keys(request.requested_changes).map(f => formatFieldName(f)).join(', ')}
                    </TableCell>
                    <TableCell>{<DateText value={request.requested_at} />}</TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell>
                      {request.reviewed_at ? <DateText value={request.reviewed_at} /> : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auto.misc.rejectProfileChange')}</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this profile change request from {selectedRequest?.employee_name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              placeholder={t('auto.misc.enterReasonForRejectionOptional')}
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              rows={3}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)} data-testid="dialog-cancel-btn">
              {t('auto.misc.cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={processing}
             data-testid="dialog-reject-btn">
              {processing ? t('auto.misc.rejecting') : t('auto.misc.rejectChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfileChangeRequests;
